import React, {useEffect, useState} from 'react';
import {useForm} from 'react-hook-form';
import {IonItem} from '@ionic/react';
import {FORM_FIELDS, SignType} from './sign.types';
import Modal from '../../components/modal/modal.component';
import Info from '../../components/info/info.component';
import {StyledIonLabel} from '@components/form/input/label.style';
import {StyledIonCheckbox} from '@components/form/input/checkbox.style';
import {StyledFooter} from '@components/pane/pane.style';
import Form from '../../components/form'
import {StyledPinRow} from './sign.style';
import CloseImage from "../../assets/images/e-remove.svg";
import {StyledModalContent} from "@components/modal/modal.style";
import {Preferences} from "@capacitor/preferences";
import {ifNull} from "@utils/tools/numbers";
import {disableSwipeToClose} from "@utils/tools/modals";

const DEFAULT_PHONE_PREFIX = '48';

type SignModalProps = {
    configuration: ISignModalConfigration;
    isOpen: boolean;
    onClose: () => void;
    onSave?: (code: string) => void;
    formSubmitted?: boolean;
};

export interface ISignModalConfigration {
    title: string;
    info?: string;
    buttonText: string;
    approvementText?: string;
    beforeCodeText?: string;
    afterCodeText?: string;
    phone?: string;
    email?: string;
    signType?: SignType;
};

export interface ISignModalFormData {
    pin1: string;
    pin2: string;
    pin3: string;
    pin4: string;
};


const SignModal: React.FC<SignModalProps> = (props: SignModalProps) => {
    const [showSign, updateShowSign] = useState<boolean>(false);
    const [accepted, updateAccepted] = useState<boolean>(false);

    const {register, handleSubmit, formState: {isValid}} = useForm({
        mode: 'onChange'
    });

    const [phoneNumber, updatePhoneNumber] = useState('');
    const [email, updateEmail] = useState('');

    useEffect(() => {
        getPersonalData();
        handleOpen();
    });

    const getPersonalData = async () => {
        if (props.configuration.signType) {
            if (props.configuration.signType === SignType.EMAIL && !props.configuration.email) {
                const userEmail = await Preferences.get({'key': 'email'});

                if (userEmail.value) {
                    props.configuration.email = userEmail.value;
                    props.configuration.phone = undefined;
                }
            }

            if (props.configuration.signType === SignType.PHONE && !props.configuration.phone) {
                const userPhone = await Preferences.get({'key': 'phone'});
                const userPhoneCode = await Preferences.get({'key': 'phone_code'});

                if (userPhone.value) {
                    props.configuration.phone = `${ifNull(userPhoneCode?.value) || DEFAULT_PHONE_PREFIX} ${userPhone}`;
                    props.configuration.email = undefined;
                }
            }
        }

        if (props.configuration.email) {
            const email = props.configuration.email;
            const extensionLength = email.substr(email.lastIndexOf("@") + 1).length + 1;
            const count = email.length - extensionLength;
            const endOf = email.substring(email.length - extensionLength);

            updateEmail(maskCharacters(count, endOf));
        } else if (!props.configuration.phone) {
            const phone = await Preferences.get({'key': 'phone'});
            const phoneCode = await Preferences.get({'key': 'phone_code'});

            const phoneNumber = `${ifNull(phone?.value) || ''}`;
            const count = phoneNumber.length - 3;
            const endOf = phoneNumber.substring(phoneNumber.length - 3);

            let resultPhone = maskCharacters(count, endOf)
            resultPhone = `${ifNull(phoneCode?.value) || DEFAULT_PHONE_PREFIX} ${resultPhone}`;

            updatePhoneNumber('+' + resultPhone);
        } else {
            const phoneNumber = props.configuration.phone;
            const count = phoneNumber.length - 3;
            const endOf = phoneNumber.substring(phoneNumber.length - 3);

            updatePhoneNumber('+' + maskCharacters(count, endOf));
        }
    };

    const handleOpen = () => {
        updateShowSign(props.isOpen);
    };

    const handleClose = () => {
        props.onClose();
    };

    const onSubmit = (data: ISignModalFormData) => {
        const onSave = props.onSave;
        if (onSave) {
            let code = data.pin1 + data.pin2 + data.pin3 + data.pin4;
            onSave(code);
        }
    };

    function handleKeyUp(event: React.KeyboardEvent<HTMLInputElement>) {
        const input = event.target as HTMLInputElement;

        if (input.value?.length > 1) {
            input.value = input.value.substring(0, 1);
            event.preventDefault();
        }

        if (input.value?.length === 1) {
            const form = input.form;
            const index = Array.prototype.indexOf.call(form, event.target);

            if (form?.elements[index + 1] && form?.elements[index + 1].tagName === "INPUT" &&
                (form?.elements[index + 1] as HTMLInputElement).name !== 'accept') {
                (form?.elements[index + 1] as HTMLInputElement).focus();
            } else {
                input.blur();
            }

            event.preventDefault();
        }

        if (input.value?.length === 0) {
            const form = input.form;
            const index = Array.prototype.indexOf.call(form, event.target);

            if (form?.elements[index - 1] && form?.elements[index - 1].tagName === "INPUT" &&
                (form?.elements[index - 1] as HTMLInputElement).name !== 'accept') {
                (form?.elements[index - 1] as HTMLInputElement).focus();
            } else {
                input.blur();
            }

            event.preventDefault();
        }
    }

    const maskCharacters = (count: number, endOfString: string) => {
        let result = '';
        for (let i = 0; i < count; i++) {
            result += "*";
        }
        result += endOfString;

        return result;
    }

    return (
        <Modal
            isOpen={showSign}
            canDismiss={disableSwipeToClose}
            onDidDismiss={() => handleClose()}>
            <Form.Container onSubmit={handleSubmit(onSubmit)}>
                <StyledModalContent>
                    <img src={CloseImage} className="close" onClick={() => handleClose()}/>
                    <h1 className="title full">{props.configuration.title}</h1>
                    <Info>{props.configuration.info} {email ? email : phoneNumber}</Info>
                    <StyledPinRow>
                        <Form.PinInput name={FORM_FIELDS.PIN_1} placeholder=" " type="text" pattern="[0-9]*"
                                       inputMode="tel"
                                       ref={register({required: true})} maxLength={1} onKeyUp={handleKeyUp}/>
                        <Form.PinInput name={FORM_FIELDS.PIN_2} placeholder=" " type="text" pattern="[0-9]*"
                                       inputMode="tel"
                                       ref={register({required: true})} maxLength={1} onKeyUp={handleKeyUp}/>
                        <Form.PinInput name={FORM_FIELDS.PIN_3} placeholder=" " type="text" pattern="[0-9]*"
                                       inputMode="tel"
                                       ref={register({required: true})} maxLength={1} onKeyUp={handleKeyUp}/>
                        <Form.PinInput name={FORM_FIELDS.PIN_4} placeholder=" " type="text" pattern="[0-9]*"
                                       inputMode="tel"
                                       ref={register({required: true})} maxLength={1} onKeyUp={handleKeyUp}/>
                    </StyledPinRow>

                    <div>
                        {
                            props.configuration.approvementText &&
                            <IonItem lines="none">
                                <StyledIonLabel
                                    className="ion-text-wrap">{props.configuration.approvementText}</StyledIonLabel>
                                <StyledIonCheckbox name={FORM_FIELDS.ACCEPT} slot="start" onIonChange={() => {
                                    updateAccepted(!accepted);
                                }}/>
                            </IonItem>
                        }
                    </div>

                    <>
                        {
                            props.configuration?.afterCodeText && <Info className="stacked transparent"><span
                                dangerouslySetInnerHTML={{__html: props.configuration.afterCodeText || ''}}/></Info>
                        }
                    </>

                    <StyledFooter className="modal">
                        <Form.Button type="submit"
                                     disabled={!isValid || props.formSubmitted || (!!props.configuration.approvementText && !accepted)}>{props.configuration.buttonText}</Form.Button>
                    </StyledFooter>
                </StyledModalContent>
            </Form.Container>
        </Modal>
    );
};

export default SignModal;